import React, { useEffect, useState, useMemo } from 'react';
import './App.css';

// CHART IMPORTS
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

// AXIOS IMPORT
import axios from 'axios';

// Register chart.js components
ChartJS.register(
    LineElement,
    PointElement,
    CategoryScale,
    LinearScale,
    Title,
    Tooltip,
    Legend
);

// Note: Using localStorage caching now for persistent caching across refreshes.

/*************************************************************************
 * BADGE IMAGE IMPORTS (adjust or remove if you don't have them)
 *************************************************************************/
import nochillBadge from './assets/images/nochill.png';
import nochillioBadge from './assets/images/nochillio.png';

// Map badge names to local images. Make sure these keys match row.badge values.
const badgeIcons = {
  nochill: nochillBadge,
  nochillio: nochillioBadge,
};

/*************************************************************************
 * COUNTDOWN TIMER COMPONENT (NEXT REFRESH)
 *************************************************************************/
function CountdownTimer() {
  const [timeLeft, setTimeLeft] = useState('');

  useEffect(() => {
    const updateTimer = () => {
      const now = new Date();
      let nextExecution = new Date(now);

      // Determine next execution hour. Cron schedule: 0 */6 * * *
      // i.e. at minute 0 of hours 0, 6, 12, 18.
      const currentHour = now.getHours();
      let nextHour = Math.floor(currentHour / 6) * 6 + 6;
      if (nextHour >= 24) {
        nextHour = nextHour % 24;
        nextExecution.setDate(nextExecution.getDate() + 1);
      }
      nextExecution.setHours(nextHour, 0, 0, 0);

      // Calculate the difference in milliseconds
      const diff = nextExecution - now;
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      // Format as HH:MM:SS
      const formatted = `${hours.toString().padStart(2, '0')}:${minutes
          .toString()
          .padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;

      setTimeLeft(formatted);
    };

    updateTimer();
    const timerId = setInterval(updateTimer, 1000);
    return () => clearInterval(timerId);
  }, []);

  return <span>{timeLeft}</span>;
}

function App() {
  // Set favicon to use the same image as the nochillio badge icon
  useEffect(() => {
    const favicon = document.querySelector("link[rel*='icon']");
    if (favicon) {
      favicon.href = nochillioBadge;
    } else {
      const newLink = document.createElement("link");
      newLink.rel = "icon";
      newLink.href = nochillioBadge;
      document.head.appendChild(newLink);
    }
  }, []);

  /*************************************************************************
   * CONFIG / CONSTANTS
   *************************************************************************/
      // IMPORTANT: Replace this with your *publicly accessible* server URL.
  const API_BASE = 'https://api.looksrarenfts.com';
  // const API_BASE = 'http://localhost:3001';

  /*************************************************************************
   * STATE: For your main metrics (unused for LEADERBOARD front end)
   *************************************************************************/
      // Updated these states to be numeric values instead of objects.
  const [badgeEligible, setBadgeEligible] = useState(0);
  const [badgeHolders, setBadgeHolders] = useState(0);
  const [badgeQueues, setBadgeQueues] = useState(0);
  const [badgeLimits, setBadgeLimits] = useState({ nochill: 0, nochillio: 0 });

  // Tables
  const [nochillBadges, setNochillBadges] = useState([]);
  const [recentChanges, setRecentChanges] = useState([]);
  const [blockNumbers, setBlockNumbers] = useState([]);
  const [nochillHolderBalances, setNochillHolderBalances] = useState([]);

  // For user-based queries (unused for LEADERBOARD front end)
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [userSnapshots, setUserSnapshots] = useState([]);
  const [userNfts, setUserNfts] = useState([]);
  const [userBalanceHistory, setUserBalanceHistory] = useState([]);
  const [userHistory, setUserHistory] = useState([]);

  /*************************************************************************
   * SEARCH & FILTER (ALIAS) STATE
   *************************************************************************/
  const [searchText, setSearchText] = useState('');
  const [aliasFilter, setAliasFilter] = useState([]);
  const [isAliasFilterOpen, setIsAliasFilterOpen] = useState(false);
  const [aliasFilterSearch, setAliasFilterSearch] = useState('');

  /*************************************************************************
   * SORT STATE
   *************************************************************************/
  const [sortConfig, setSortConfig] = useState({
    column: null, // one of "rank", "spectrum", "nochillio", "nochill", "width", "redacted"
    direction: 'asc', // or 'desc'
  });

  /*************************************************************************
   * CUSTOM USER DROPDOWN STATE (unused for LEADERBOARD front end)
   *************************************************************************/
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
  const [userDropdownSearch, setUserDropdownSearch] = useState('');

  /*************************************************************************
   * MODAL STATE for "OVERRIDE YOUR WALLET"
   *************************************************************************/
  const [showOverrideModal, setShowOverrideModal] = useState(false);

  // When user clicks "OVERRIDE YOUR WALLET" link
  const handleOverrideClick = (e) => {
    e.preventDefault(); // Prevent direct navigation
    setShowOverrideModal(true);
  };

  // "I Agree" → open link in new tab, close modal
  const handleAgree = () => {
    window.open('https://id.nochill.io/authenticate', '_blank');
    setShowOverrideModal(false);
  };

  // "Cancel" → just close modal
  const handleCancel = () => {
    setShowOverrideModal(false);
  };

  // When user clicks outside dropdown (for user dropdown)
  useEffect(() => {
    function handleClickOutside(e) {
      if (
          !e.target.closest('.user-dropdown-container') &&
          !e.target.closest('.user-select-row')
      ) {
        closeUserDropdown();
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  /*************************************************************************
   * DEBUG: On mount, log environment details
   *************************************************************************/
  useEffect(() => {
    console.log('=== ENVIRONMENT DETAILS (MOUNT) ===');
    console.log('User Agent:', navigator.userAgent);
    console.log('Window Location:', window.location.href);
    console.log('API_BASE:', API_BASE);
  }, [API_BASE]);

  /*************************************************************************
   * FETCH: On mount, load only LEADERBOARD data
   *************************************************************************/
  useEffect(() => {
    // Only call the leaderboard API for the front end
    fetchNochillBadges();

    // The following API calls are not required for the LEADERBOARD
    // Uncomment if you need them later
    fetchBadgeEligible();
    fetchBadgeHolders();
    fetchBadgeQueues();
    fetchBadgeLimits();
    // fetchRecentChanges();
    // fetchBlockNumbers();
    // fetchNochillHolderBalances();
    // fetchAllUsers();
  }, []);

  /*************************************************************************
   * FETCH: When user changes, load user-specific metrics (not used)
   *************************************************************************/
  useEffect(() => {
    if (selectedUser) {
      // fetchUserSnapshots(selectedUser);
      // fetchUserNfts(selectedUser);
      // fetchUserBalanceHistory(selectedUser);
      // fetchUserHistory(selectedUser);
    } else {
      setUserSnapshots([]);
      setUserNfts([]);
      setUserBalanceHistory([]);
      setUserHistory([]);
    }
  }, [selectedUser]);

  /*************************************************************************
   * API CALLS (AXIOS)
   *************************************************************************/

  /**
   * If running on localhost:3001, skip localStorage caching.
   * Otherwise, use localStorage caching with a 5-minute expiration.
   */
  const shouldUseCache = API_BASE !== 'http://localhost:3001';

  // --- LEADERBOARD API CALL (conditional localStorage caching) ---
  async function fetchNochillBadges() {
    const url = `${API_BASE}/api/nochill-badges`;
    console.log(`fetchNochillBadges → GET ${url}`);

    if (shouldUseCache) {
      const now = Date.now();
      // Attempt to retrieve cached data from localStorage
      const cachedData = localStorage.getItem('leaderboardCache');
      if (cachedData) {
        const { timestamp, data } = JSON.parse(cachedData);
        // Check if the cached data is valid (less than 5 minutes old)
        if (now - timestamp < 300000) {
          console.log('Using cached leaderboard data.');
          setNochillBadges(data);
          return;
        }
      }
    }

    try {
      const response = await axios.get(url);
      console.log('fetchNochillBadges → success:', response.data);
      if (shouldUseCache) {
        // Cache the data along with the current timestamp in localStorage
        const now = Date.now();
        localStorage.setItem(
            'leaderboardCache',
            JSON.stringify({
              timestamp: now,
              data: response.data,
            })
        );
      }
      setNochillBadges(response.data);
    } catch (err) {
      console.error('fetchNochillBadges → ERROR:', err);
    }
  }

  // --- The following calls also conditionally use localStorage caching ---
  async function fetchBadgeEligible() {
    const url = `${API_BASE}/api/badge-eligible`;
    console.log(`fetchBadgeEligible → GET ${url}`);

    if (shouldUseCache) {
      const now = Date.now();
      const cachedData = localStorage.getItem('badgeEligibleCache');
      if (cachedData) {
        const { timestamp, data } = JSON.parse(cachedData);
        if (now - timestamp < 300000) {
          console.log('Using cached badgeEligible data.');
          setBadgeEligible(data);
          return;
        }
      }
    }

    try {
      const response = await axios.get(url);
      // Extract the numeric value from the returned object (e.g. { nochill: value })
      const value = Number(response.data.nochill) || 0;
      console.log('fetchBadgeEligible → success:', value);
      if (shouldUseCache) {
        const now = Date.now();
        localStorage.setItem(
            'badgeEligibleCache',
            JSON.stringify({
              timestamp: now,
              data: value,
            })
        );
      }
      setBadgeEligible(value);
    } catch (err) {
      console.error('fetchBadgeEligible → ERROR:', err);
    }
  }

  async function fetchBadgeHolders() {
    const url = `${API_BASE}/api/badge-holders`;
    console.log(`fetchBadgeHolders → GET ${url}`);

    if (shouldUseCache) {
      const now = Date.now();
      const cachedData = localStorage.getItem('badgeHoldersCache');
      if (cachedData) {
        const { timestamp, data } = JSON.parse(cachedData);
        if (now - timestamp < 300000) {
          console.log('Using cached badgeHolders data.');
          setBadgeHolders(data);
          return;
        }
      }
    }

    try {
      const response = await axios.get(url);
      // The API returns an object with a "count" property
      const value = Number(response.data.count) || 0;
      console.log('fetchBadgeHolders → success:', value);
      if (shouldUseCache) {
        const now = Date.now();
        localStorage.setItem(
            'badgeHoldersCache',
            JSON.stringify({
              timestamp: now,
              data: value,
            })
        );
      }
      setBadgeHolders(value);
    } catch (err) {
      console.error('fetchBadgeHolders → ERROR:', err);
    }
  }

  async function fetchBadgeQueues() {
    const url = `${API_BASE}/api/badge-queues`;
    console.log(`fetchBadgeQueues → GET ${url}`);

    if (shouldUseCache) {
      const now = Date.now();
      const cachedData = localStorage.getItem('badgeQueuesCache');
      if (cachedData) {
        const { timestamp, data } = JSON.parse(cachedData);
        if (now - timestamp < 300000) {
          console.log('Using cached badgeQueues data.');
          setBadgeQueues(data);
          return;
        }
      }
    }

    try {
      const response = await axios.get(url);
      // The API returns an object with a "count" property
      const value = Number(response.data.count) || 0;
      console.log('fetchBadgeQueues → success:', value);
      if (shouldUseCache) {
        const now = Date.now();
        localStorage.setItem(
            'badgeQueuesCache',
            JSON.stringify({
              timestamp: now,
              data: value,
            })
        );
      }
      setBadgeQueues(value);
    } catch (err) {
      console.error('fetchBadgeQueues → ERROR:', err);
    }
  }

  async function fetchBadgeLimits() {
    const url = `${API_BASE}/api/badge-limits`;
    console.log(`fetchBadgeLimits → GET ${url}`);

    if (shouldUseCache) {
      const now = Date.now();
      const cachedData = localStorage.getItem('badgeLimitsCache');
      if (cachedData) {
        const { timestamp, data } = JSON.parse(cachedData);
        if (now - timestamp < 300000) {
          console.log('Using cached badgeLimits data.');
          setBadgeLimits(data);
          return;
        }
      }
    }

    try {
      const response = await axios.get(url);
      console.log('fetchBadgeLimits → success:', response.data);
      if (shouldUseCache) {
        const now = Date.now();
        localStorage.setItem(
            'badgeLimitsCache',
            JSON.stringify({
              timestamp: now,
              data: response.data,
            })
        );
      }
      setBadgeLimits(response.data);
    } catch (err) {
      console.error('fetchBadgeLimits → ERROR:', err);
    }
  }

  /*
  async function fetchRecentChanges() {
    const url = `${API_BASE}/api/recent-changes`;
    console.log(`fetchRecentChanges → GET ${url}`);
    try {
      const response = await axios.get(url);
      console.log('fetchRecentChanges → success:', response.data);
      setRecentChanges(response.data);
    } catch (err) {
      console.error('fetchRecentChanges → ERROR:', err);
    }
  }

  async function fetchBlockNumbers() {
    const url = `${API_BASE}/api/block-number`;
    console.log(`fetchBlockNumbers → GET ${url}`);
    try {
      const response = await axios.get(url);
      console.log('fetchBlockNumbers → success:', response.data);
      setBlockNumbers(response.data);
    } catch (err) {
      console.error('fetchBlockNumbers → ERROR:', err);
    }
  }

  async function fetchNochillHolderBalances() {
    const url = `${API_BASE}/api/nochill-holder-balances`;
    console.log(`fetchNochillHolderBalances → GET ${url}`);
    try {
      const response = await axios.get(url);
      console.log('fetchNochillHolderBalances → success:', response.data);
      setNochillHolderBalances(response.data);
    } catch (err) {
      console.error('fetchNochillHolderBalances → ERROR:', err);
    }
  }

  async function fetchAllUsers() {
    const url = `${API_BASE}/api/users`;
    console.log(`fetchAllUsers → GET ${url}`);
    try {
      const response = await axios.get(url);
      console.log('fetchAllUsers → success:', response.data);
      setUsers(response.data);
      if (response.data.length > 0) {
        const randomIndex = Math.floor(Math.random() * response.data.length);
        setSelectedUser(response.data[randomIndex]);
      }
    } catch (err) {
      console.error('fetchAllUsers → ERROR:', err);
    }
  }

  async function fetchUserSnapshots(username) {
    const url = `${API_BASE}/api/total-consecutive-snapshots?username=${encodeURIComponent(
      username
    )}`;
    console.log(`fetchUserSnapshots → GET ${url}`);
    try {
      const response = await axios.get(url);
      console.log('fetchUserSnapshots → success:', response.data);
      setUserSnapshots(response.data);
    } catch (err) {
      console.error('fetchUserSnapshots → ERROR:', err);
    }
  }

  async function fetchUserNfts(username) {
    const url = `${API_BASE}/api/nochillio-nfts?username=${encodeURIComponent(
      username
    )}`;
    console.log(`fetchUserNfts → GET ${url}`);
    try {
      const response = await axios.get(url);
      console.log('fetchUserNfts → success:', response.data);
      setUserNfts(response.data);
    } catch (err) {
      console.error('fetchUserNfts → ERROR:', err);
    }
  }

  async function fetchUserBalanceHistory(username) {
    const url = `${API_BASE}/api/nochill-balance-history?username=${encodeURIComponent(
      username
    )}`;
    console.log(`fetchUserBalanceHistory → GET ${url}`);
    try {
      const response = await axios.get(url);
      console.log('fetchUserBalanceHistory → success:', response.data);
      setUserBalanceHistory(response.data);
    } catch (err) {
      console.error('fetchUserBalanceHistory → ERROR:', err);
    }
  }

  async function fetchUserHistory(username) {
    const url = `${API_BASE}/api/user-history?username=${encodeURIComponent(username)}`;
    console.log(`fetchUserHistory → GET ${url}`);
    try {
      const response = await axios.get(url);
      console.log('fetchUserHistory → success:', response.data);
      setUserHistory(response.data);
    } catch (err) {
      console.error('fetchUserHistory → ERROR:', err);
    }
  }
  */

  /*************************************************************************
   * DERIVED DATA: ALIAS FILTER OPTIONS
   *************************************************************************/
  const uniqueAliases = useMemo(() => {
    // Generate unique aliases then sort them alphabetically, case insensitive
    const aliases = Array.from(new Set(nochillBadges.map((row) => row.username)));
    return aliases.sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
  }, [nochillBadges]);

  /*************************************************************************
   * HANDLERS
   *************************************************************************/
  const handleSearchChange = (e) => {
    setSearchText(e.target.value);
  };

  const toggleAliasFilter = () => {
    setIsAliasFilterOpen((prev) => !prev);
  };

  const handleAliasValueChange = (val) => {
    setAliasFilter((prev) => {
      if (prev.includes(val)) {
        return prev.filter((x) => x !== val);
      } else {
        return [...prev, val];
      }
    });
  };

  const handleAliasFilterReset = () => {
    setAliasFilter([]);
    setAliasFilterSearch('');
  };

  const handleAliasFilterClose = () => {
    setIsAliasFilterOpen(false);
  };

  const handleSort = (column) => {
    setSortConfig((prev) => {
      if (prev.column === column) {
        return {
          column,
          direction: prev.direction === 'asc' ? 'desc' : 'asc',
        };
      } else {
        return { column, direction: 'asc' };
      }
    });
  };

  // For custom user dropdown (unused for LEADERBOARD)
  const toggleUserDropdown = () => {
    setIsUserDropdownOpen((prev) => !prev);
  };
  const closeUserDropdown = () => {
    setIsUserDropdownOpen(false);
  };
  const handleUserSelect = (user) => {
    setSelectedUser(user);
    closeUserDropdown();
  };

  // Sort + filter the user list for the dropdown (unused for LEADERBOARD)
  const sortedFilteredUsers = useMemo(() => {
    let data = [...users].sort((a, b) =>
        a.toLowerCase().localeCompare(b.toLowerCase())
    );
    if (userDropdownSearch.trim() !== '') {
      data = data.filter((u) =>
          u.toLowerCase().includes(userDropdownSearch.toLowerCase())
      );
    }
    return data;
  }, [users, userDropdownSearch]);

  /*************************************************************************
   * FILTER + SORT (for Leaderboard)
   *************************************************************************/
  const filteredBadges = useMemo(() => {
    return nochillBadges.filter((row) => {
      if (
          searchText &&
          !row.username.toLowerCase().includes(searchText.toLowerCase())
      ) {
        return false;
      }
      if (aliasFilter.length > 0 && !aliasFilter.includes(row.username)) {
        return false;
      }
      return true;
    });
  }, [nochillBadges, searchText, aliasFilter]);

  const sortedBadges = useMemo(() => {
    const data = [...filteredBadges];
    if (sortConfig.column) {
      data.sort((a, b) => {
        const { column } = sortConfig;
        if (column === 'rank') {
          const aVal = parseInt(a.rank, 10) || 0;
          const bVal = parseInt(b.rank, 10) || 0;
          return aVal - bVal;
        } else if (column === 'spectrum') {
          const aVal = parseInt(a.spectrum, 10) || 0;
          const bVal = parseInt(b.spectrum, 10) || 0;
          return aVal - bVal;
        } else if (column === 'nochillio') {
          const aVal = parseFloat(a.nochillio) || 0;
          const bVal = parseFloat(b.nochillio) || 0;
          return aVal - bVal;
        } else if (column === 'nochill') {
          const aVal = parseFloat(a.nochill) || 0;
          const bVal = parseFloat(b.nochill) || 0;
          return aVal - bVal;
        } else if (column === 'width') {
          const aVal = parseFloat(a.total_points) || 0;
          const bVal = parseFloat(b.total_points) || 0;
          return aVal - bVal;
        } else if (column === 'redacted') {
          const aVal = parseFloat(a.redacted) || 0;
          const bVal = parseFloat(b.redacted) || 0;
          return aVal - bVal;
        }
        return 0;
      });
      if (sortConfig.direction === 'desc') {
        data.reverse();
      }
    }
    return data;
  }, [filteredBadges, sortConfig]);

  // Display all sorted rows without any pagination or record limit.
  const displayedBadges = useMemo(() => {
    return sortedBadges;
  }, [sortedBadges]);

  /*************************************************************************
   * RENDER
   *************************************************************************/
  return (
      <div className="app-container">
        {/* HEADER */}
        <header className="header">
          <a href="https://nochill.io" target="_blank" rel="noopener noreferrer">
            <img
                src="https://nochill.io/images/assets/worldorder.png"
                alt="NOCHILL Logo"
            />
          </a>
          {/* Updated link → triggers a modal instead of direct navigation */}
          <a className="override-link" href="#" onClick={handleOverrideClick}>
            LINK WALLET
          </a>
        </header>

        {/* MODAL (only visible if showOverrideModal === true) */}
        {showOverrideModal && (
            <div className="modal-overlay">
              <div className="modal-content">
                <p>
                  The default wallet for scoring on the leaderboard is the dynamic wallet in The Arena App.
                </p>
                <p>
                  By clicking <strong>I Agree</strong>, you acknowledge that if you link a wallet, only
                  the assets contained within will be used for scoring on the leaderboard.
                </p>
                <p></p>
                <p>
                  <strong>IMPORTANT:</strong> Click the "Link Wallet to X" button after connecting or
                  your entry will not be saved.
                </p>
                <div className="modal-buttons">
                  <button onClick={handleAgree}>I Agree</button>
                  <button onClick={handleCancel}>Cancel</button>
                </div>
              </div>
            </div>
        )}

        {/* STAT CARDS */}
        <div className="stat-cards">
          {/* NOCHILLIOS StatCard removed as requested */}
        </div>

        {/* LEADERBOARD SECTION */}
        <div className="table-container">
          <section className="section">
            {/* Leaderboard heading with the countdown timer to the right */}
            <h2 style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              LEADERBOARD-<span className="next-refresh-text"><CountdownTimer /></span>
            </h2>
            <div className="table-response-wrapper">
              <table className="custom-table">
                <thead>
                {/* Search Row */}
                <tr>
                  <th colSpan="7">
                    <div className="table-search-container">
                      <input
                          type="text"
                          placeholder="Search by username..."
                          value={searchText}
                          onChange={handleSearchChange}
                          className="search-input"
                      />
                    </div>
                  </th>
                </tr>
                {/* Header Row in new order: RANK, ALIAS, WIDTH, NOCHILL, NOCHILLIO, SPECTRUM, REDACTED */}
                <tr>
                  <th style={{ width: '60px' }}>
                    RANK
                    <SortIcon
                        onClick={() => handleSort('rank')}
                        isActive={sortConfig.column === 'rank'}
                        direction={sortConfig.direction}
                    />
                  </th>
                  <th>
                    ALIAS
                    <span className="filter-icon" onClick={toggleAliasFilter}>
                      {isAliasFilterOpen ? '▲' : '▼'}
                    </span>
                    {isAliasFilterOpen && (
                        <AliasFilterDropdown
                            allValues={uniqueAliases}
                            selectedValues={aliasFilter}
                            filterText={aliasFilterSearch}
                            onFilterTextChange={setAliasFilterSearch}
                            onValueChange={handleAliasValueChange}
                            onReset={handleAliasFilterReset}
                            onClose={handleAliasFilterClose}
                        />
                    )}
                  </th>
                  <th>
                    WIDTH
                    <SortIcon
                        onClick={() => handleSort('width')}
                        isActive={sortConfig.column === 'width'}
                        direction={sortConfig.direction}
                    />
                  </th>
                  <th>
                    NOCHILL POINTS
                    <SortIcon
                        onClick={() => handleSort('nochill')}
                        isActive={sortConfig.column === 'nochill'}
                        direction={sortConfig.direction}
                    />
                  </th>
                  <th>
                    NOCHILLIO POINTS
                    <SortIcon
                        onClick={() => handleSort('nochillio')}
                        isActive={sortConfig.column === 'nochillio'}
                        direction={sortConfig.direction}
                    />
                  </th>
                  <th>
                    SPECTRUM POINTS
                    <SortIcon
                        onClick={() => handleSort('spectrum')}
                        isActive={sortConfig.column === 'spectrum'}
                        direction={sortConfig.direction}
                    />
                  </th>
                  <th>
                    REDACTED
                    <SortIcon
                        onClick={() => handleSort('redacted')}
                        isActive={sortConfig.column === 'redacted'}
                        direction={sortConfig.direction}
                    />
                  </th>
                </tr>
                </thead>
                <tbody>
                {displayedBadges.map((row, idx) => {
                  // Use the rank provided from the API; if not available, display '???'
                  const formattedRank =
                      row.rank != null ? row.rank.toLocaleString() : '???';

                  // For NOCHILL and NOCHILLIO POINTS:
                  // If the value is an integer, show no decimals. Otherwise, 2 decimals.
                  // Also include commas via toLocaleString.
                  const numericNochill = parseFloat(row.nochill) || 0;
                  const formattedNochill = numericNochill.toLocaleString(undefined, {
                    minimumFractionDigits: Number.isInteger(numericNochill) ? 0 : 2,
                    maximumFractionDigits: 2,
                  });

                  const numericNochillio = parseFloat(row.nochillio) || 0;
                  const formattedNochillio = numericNochillio.toLocaleString(undefined, {
                    minimumFractionDigits: Number.isInteger(numericNochillio) ? 0 : 2,
                    maximumFractionDigits: 2,
                  });

                  // For WIDTH, display without decimals but with commas
                  const numericWidth = parseFloat(row.total_points) || 0;
                  const formattedWidth = numericWidth.toLocaleString(undefined, {
                    maximumFractionDigits: 0,
                  });

                  // SPECTRUM as integer with commas
                  const numericSpectrum = parseInt(row.spectrum, 10) || 0;
                  const formattedSpectrum = numericSpectrum.toLocaleString();

                  // REDACTED rounded to exactly two decimal places
                  const numericRedacted = parseFloat(row.redacted) || 0;
                  const formattedRedacted = numericRedacted.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  });

                  // If row.badge is "nochill" or "nochillio", pick from badgeIcons
                  const badgeSrc = row.badge && badgeIcons[row.badge];

                  return (
                      <tr key={idx}>
                        <td className="numeric-cell">{formattedRank}</td>
                        <td>
                          <div className="alias-wrapper">
                            {/* Twitter PFP on the far left if available */}
                            {row.twitter_pfp_url && (
                                <img
                                    src={row.twitter_pfp_url}
                                    alt="Twitter PFP"
                                    className="alias-pfp"
                                />
                            )}

                            {/* Alias text block (Twitter name if available, then @username) */}
                            <div className="alias-text">
                              {row.twitter_username && (
                                  <div className="alias-twitter">
                                    <a
                                        href={`https://x.com/${row.username.replace(/^@/, '')}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="alias-twitter-link"
                                    >
                                      {row.twitter_username}
                                    </a>
                                  </div>
                              )}

                              {/* Put the badge icon IMMEDIATELY after @username */}
                              <div className="alias-row">
                                <a
                                    href={`https://arena.social/${row.username}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="alias-username"
                                >
                                  @{row.username}
                                </a>
                                {badgeSrc && (
                                    <img
                                        src={badgeSrc}
                                        alt={`${row.badge} badge`}
                                        className="badge-icon-inline"
                                    />
                                )}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="numeric-cell">{formattedWidth}</td>
                        <td className="numeric-cell">{formattedNochill}</td>
                        <td className="numeric-cell">{formattedNochillio}</td>
                        <td className="numeric-cell">{formattedSpectrum}</td>
                        <td className="numeric-cell">{formattedRedacted}</td>
                      </tr>
                  );
                })}
                </tbody>
              </table>
            </div>
          </section>
        </div>

        {/*
      Additional components or sections (such as USER-BASED METRICS, charts, etc.)
      can be added here. For example:

      <div className="table-container">
        <section className="section">
          <h2>METRICS</h2>
          ...
        </section>
      </div>
    */}
      </div>
  );
}

/*************************************************************************
 * SMALL COMPONENTS
 *************************************************************************/

/**
 * AliasFilterDropdown for the ALIAS column.
 * Includes a text input for filtering, a Reset button, a list of checkboxes, and a Close button.
 */
function AliasFilterDropdown({
                               allValues,
                               selectedValues,
                               filterText,
                               onFilterTextChange,
                               onValueChange,
                               onReset,
                               onClose,
                             }) {
  // Sort the filtered values alphabetically, case insensitive
  const filteredValues = useMemo(() => {
    let values = !filterText
        ? allValues
        : allValues.filter((val) =>
            val.toLowerCase().includes(filterText.toLowerCase())
        );
    return values.sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
  }, [allValues, filterText]);

  return (
      <div className="filter-dropdown">
        <button className="filter-reset-button" onClick={onReset}>
          Reset
        </button>
        <input
            type="text"
            placeholder="Search..."
            value={filterText}
            onChange={(e) => onFilterTextChange(e.target.value)}
            className="filter-search-input"
        />
        <div className="filter-values-list">
          {filteredValues.map((val) => {
            const isChecked = selectedValues.includes(val);
            return (
                <label key={val} className="filter-value-item">
                  <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={() => onValueChange(val)}
                  />
                  <span>{val}</span>
                </label>
            );
          })}
          {filteredValues.length === 0 && (
              <div className="no-match-text">No matches</div>
          )}
        </div>
        <button className="filter-close-button" onClick={onClose}>
          Close
        </button>
      </div>
  );
}

/**
 * SortIcon for columns RANK, SPECTRUM, NOCHILLIO, NOCHILL, WIDTH, REDACTED.
 * Shows a neutral icon if inactive; if active, shows ascending (▲) or descending (▼).
 */
function SortIcon({ onClick, isActive, direction }) {
  let icon = '⇅';
  if (isActive) {
    icon = direction === 'asc' ? '▲' : '▼';
  }
  return (
      <span className="sort-icon" onClick={onClick}>
      {icon}
    </span>
  );
}

/*************************************************************************
 * StatCard COMPONENT
 * Updated so that it simply displays the numeric value without showing the API column name.
 *************************************************************************/
function StatCard({ title, data }) {
  return (
      <div className="stat-card">
        <h3>{title}</h3>
        <div className="stat-value">{data}</div>
      </div>
  );
}

/*************************************************************************
 * TimeSeriesChart COMPONENT
 *************************************************************************/
function TimeSeriesChart({ rawData, label, valueKey }) {
  if (!rawData || !rawData.length) {
    return <p>No data to display.</p>;
  }
  const sortedData = [...rawData].sort(
      (a, b) => new Date(a.created_at) - new Date(b.created_at)
  );
  // Custom format: "MM/YY/DD <hour><AM/PM>"
  const formatTimestamp = (dateString) => {
    const date = new Date(dateString);
    const month = ('0' + (date.getMonth() + 1)).slice(-2);
    const day = ('0' + date.getDate()).slice(-2);
    const year = date.getFullYear().toString().slice(-2);
    let hour = date.getHours();
    const ampm = hour >= 12 ? 'PM' : 'AM';
    hour = hour % 12;
    if (hour === 0) hour = 12;
    return `${month}/${year}/${day} ${hour}${ampm}`;
  };

  const chartData = {
    labels: sortedData.map((d) => formatTimestamp(d.created_at)),
    datasets: [
      {
        label,
        data: sortedData.map((d) => parseFloat(d[valueKey])),
        borderColor: '#66ccff',
        backgroundColor: 'rgba(102, 204, 255, 0.2)',
        fill: true,
        tension: 0.2,
        pointRadius: 3,
      },
    ],
  };

  const options = {
    responsive: true,
    scales: {
      x: {
        title: { display: true, text: 'Timestamp' },
        ticks: {
          autoSkip: true,
          maxTicksLimit: 3,
        },
      },
      y: {
        title: { display: true, text: label },
        ticks: {
          stepSize: 1,
          autoSkip: true,
          maxTicksLimit: 5,
          callback: (value) => value,
        },
        afterDataLimits: (scale) => {
          if (scale.min === scale.max) {
            scale.min -= 1;
            scale.max += 1;
          }
        },
      },
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: true,
        callbacks: {
          label: (context) => `${label}: ${context.parsed.y}`,
        },
      },
    },
  };

  return (
      <div style={{ marginTop: '1rem' }}>
        <Line data={chartData} options={options} />
      </div>
  );
}

export default App;
